import {useMutation} from '@tanstack/react-query'

import {useSlotManagementContext} from '../../../slotManagement'
import {
  SlotValidationMutationResult,
  UseSlotValidationMutationParams
} from '../../../slotManagement/context/SlotManagementContext'
import {BatchSlotValidationResponse} from '../../../slotManagement/requests/types'

import {useOrderIntakeSlotValidationEnabled} from './useOrderIntakeSlotValidationEnabled'

/**
 * Order Intake slot validation mutation hook.
 * Provides on-demand validation for ALL dateTimes (e.g., on save).
 *
 * Gets configuration from SlotManagementContext (feature toggle, orgUnitId, siteNumber).
 *
 * Validation logic (same as query variant):
 * - Enabled when feature toggle is on and at least one slot request is valid
 *
 * Mutation is disabled when:
 * - Feature toggle is off
 * - Slot request params cannot be built (validation failed)
 * - No mutation function provided
 *
 * Side effect: Updates slot availability state IMMEDIATELY as each validation completes.
 * Uses Promise.allSettled to validate all slots in parallel. Each slot's state is updated
 * as soon as its validation response arrives, providing real-time feedback to the UI.
 *
 * @example
 * ```tsx
 * const {mutation, shouldValidate, slotAvailabilities} = useOrderIntakeSlotValidationMutation({
 *   slotValidationMutationFn: (params) => validateSlot(params)
 * })
 *
 * // Trigger validation on save
 * const handleSave = async () => {
 *   if (shouldValidate) {
 *     // No params needed - built internally using useSlotRequests
 *     // State updates happen immediately as each slot validates
 *     const result = await mutation.mutateAsync()
 *     console.log(`Validated ${result.results.length} slots`)
 *   }
 * }
 * ```
 */
export const useOrderIntakeSlotValidationMutation = ({
  slotValidationMutationFn
}: UseSlotValidationMutationParams): SlotValidationMutationResult<BatchSlotValidationResponse> => {
  const {setSlotAvailability, slotAvailabilities} = useSlotManagementContext()

  // Determine if validation should be enabled (same logic as query variant)
  const {isEnabled, slotRequestResults} = useOrderIntakeSlotValidationEnabled()

  // Get valid slot params
  const validSlots = slotRequestResults
    .map((result, index) => ({params: result.params, index}))
    .filter((item) => item.params !== null)

  // Determine if mutation should be used
  const shouldValidate = isEnabled && !!slotValidationMutationFn && validSlots.length > 0

  // Create single mutation that validates all slots using Promise.allSettled
  const mutation = useMutation<BatchSlotValidationResponse, unknown, void>({
    mutationFn: async () => {
      if (!slotValidationMutationFn) {
        throw new Error('slotValidationMutationFn is required')
      }

      // Validate all slots in parallel and update state immediately for each response
      const validationPromises = validSlots.map(({params, index}) =>
        slotValidationMutationFn(params!)
          .then((response) => {
            // Update state immediately when this slot's validation completes
            setSlotAvailability({
              dateTimeIndex: index,
              isAvailable: response.valid
            })
            return response
          })
          .catch((error) => {
            // Update state immediately when this slot's validation fails
            setSlotAvailability({
              dateTimeIndex: index,
              isAvailable: false
            })
            throw error
          })
      )

      const settledResults = await Promise.allSettled(validationPromises)

      return {
        results: validSlots.map(({index}, idx) => ({index, result: settledResults[idx]}))
      }
    }
  })

  return {
    mutation,
    shouldValidate,
    slotAvailabilities
  }
}
